import {
  CdkVirtualScrollable,
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLLABLE,
} from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  input,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  output,
  QueryList,
  Signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { startWith, Subscription } from 'rxjs';

import {
  ColumnAutosizeVirtualScrollDirective,
  VirtualScrollableRegistryService,
} from '@blaze-runtime/engine-ui-feature';
import { GROUP_BY_KEY } from '@blaze-shared/datasets-domain';
import {
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputTableColumnModel,
  PropertiesConfigInputTableConditionalFormatModel,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { getColumnKey } from '@blaze-shared/schema-domain';
import { runAfterFramePaint } from '@blaze-shared/shared-util';

import {
  CellEditErrors,
  CellEditMode,
  ColumnEditableConfig,
} from '../inline-editor/base-editor/inline-editor.types';
import { ColumnType, TableVirtualScrollFacade, VirtualTableColumn } from '../virtual-scroll';

type FormatterFn<T> = (
  item: T | null,
  columnKey: string,
) => PropertiesConfigInputTableConditionalFormatModel | undefined;

export interface CellEditEvent {
  event: MouseEvent | FocusEvent;
  columnLabel: string | undefined;
  columnName: string;
  columnId: `colId:${string}_${string}` | undefined;
  rowIndex: number;
  cell: HTMLElement;
}

@UntilDestroy()
@Component({
  selector: 'be-table-row',
  templateUrl: './table-row.component.html',
  styleUrls: ['./table-row.component.scss', './table-row-cell-formats.scss'],
  providers: [
    {
      provide: VIRTUAL_SCROLLABLE,
      useFactory: (registry: VirtualScrollableRegistryService): CdkVirtualScrollable | null =>
        registry.getInstance(),
      deps: [VirtualScrollableRegistryService],
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableRowComponent<T> implements OnChanges, AfterViewInit, OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly notificationService = inject(NotificationService);
  private readonly registry = inject(VirtualScrollableRegistryService);
  private readonly tableVirtualScrollFacade = inject(TableVirtualScrollFacade);

  rowHeightPx = input<number>();
  appendOnly = input(false);
  selected = input(false);
  virtualScrollMinBufferPx = input(200);
  virtualScrollMaxBufferPx = input(500);

  rowItem = input.required<T>();

  actions = input<PropertiesConfigInputContainerItemActionModel[]>([]);
  actionsTpl = input<TemplateRef<unknown> | null>(null);
  showActionIcons = input(false);

  rowIndex = input.required<number>();
  dataRowIndex = input.required<number>();
  hasBulkActions = input(false);
  hasRowNumbering = input(false);

  columnClasses = input<string[]>([]);
  cellTemplates = input<Record<TableColumnFormat, TemplateRef<unknown>> | null>(null);

  cellsEditConfig = input<ColumnEditableConfig>();
  cellsEditErrors = input<CellEditErrors>();
  idColumnName = input<string>();

  groupColors = input<string[]>();
  groupingColumnName = input<string>(GROUP_BY_KEY);
  isWrappedGroup = input<boolean>();
  groupColsDefs = input<PropertiesConfigInputTableColumnModel[]>();

  actionOnRowClickAvailable = input(false);
  isInInlineEditMode = input(false);

  formatters = input<FormatterFn<T> | null>();

  toggleSelection = output();

  cellEdit = output<CellEditEvent>();
  cellMouseDownToggled = output<boolean>();
  toggleWrap = output<T>();

  hoverCellRow = output<number | string | undefined>();

  @Input() @HostBinding('class.virtual-scroll-enabled') virtualScrollEnabled = true;
  @Input() @HostBinding('class.row-actions-hover') showActionsOnHoverOnly = false;

  @Input() @HostBinding('class.is-last') isLastRow = false;

  @ViewChild('viewport', { read: ElementRef }) viewport!: ElementRef<HTMLElement>;
  @ViewChild('actionsCell') actionsCell: ElementRef<HTMLElement> | null = null;
  @ViewChild(ColumnAutosizeVirtualScrollDirective)
  virtualScrollStrategy: ColumnAutosizeVirtualScrollDirective | null = null;
  @ViewChild(CdkVirtualScrollViewport) virtualScrollViewport: CdkVirtualScrollViewport | null =
    null;
  @ViewChildren('cell') cells!: QueryList<ElementRef<HTMLElement>>;

  private readonly actionsResizeSub = Subscription.EMPTY;
  private scrollable: HTMLElement | undefined;
  private tooltip?: MatTooltip;

  getColumnKey = getColumnKey;

  firstColumnsAlwaysVisible = this.tableVirtualScrollFacade.firstColumnsAlwaysVisible;
  lastColumnAlwaysVisible = this.tableVirtualScrollFacade.lastColumnAlwaysVisible;

  leftColumnsWidth = this.tableVirtualScrollFacade.cellsWidth('left');
  rightColumnsWidth = this.tableVirtualScrollFacade.cellsWidth('right');
  columnsWidth = this.tableVirtualScrollFacade.cellsWidth('scroll');

  leftColumnsTotalWidth = this.tableVirtualScrollFacade.rowWidth('left');
  rightColumnsTotalWidth = this.tableVirtualScrollFacade.rowWidth('right');
  scrollColumnsTotalWidth = this.tableVirtualScrollFacade.rowWidth('scroll');

  actionsCellWidth = this.tableVirtualScrollFacade.actionsCellMaxWidth;

  leftColumns = this.tableVirtualScrollFacade.columns('left');
  rightColumns = this.tableVirtualScrollFacade.columns('right');
  columns = this.tableVirtualScrollFacade.columns('scroll');
  gridTemplateColumns = this.tableVirtualScrollFacade.gridTemplateColumns('scroll');

  readonly indexes: Signal<Record<string, number>>;
  readonly columnKeys: Signal<string[]>;
  readonly conditionalFormatInfo: Signal<
    Record<string, PropertiesConfigInputTableConditionalFormatModel | undefined>
  >;

  trackColumn = (_index: number, column: VirtualTableColumn): string => column.name;

  get groupingLevel(): number {
    return (this.rowItem() as { __level?: number })?.['__level'] ?? -1;
  }

  constructor() {
    this.indexes = computed(() => {
      const columns = this.tableVirtualScrollFacade.columnsForSlot();
      return columns
        .filter((c) => c.type === 'cell')
        .reduce<Record<string, number>>((acc, col, i) => {
          acc[col.name] = i;
          return acc;
        }, {});
    });

    this.columnKeys = computed(() => {
      const columns = this.tableVirtualScrollFacade.columnsForSlot();
      return columns.map(({ def }) => getColumnKey(def));
    });

    this.conditionalFormatInfo = computed(() => {
      return this.groupingLevel === -1
        ? Object.fromEntries(
            this.columnKeys().map((columnKey) => [
              columnKey,
              this.formatters()?.(this.rowItem(), columnKey),
            ]),
          )
        : {};
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rowItem'] || changes['rowIndex']) {
      this.updateScrollPosition();
    }

    if (changes['actions']) {
      this.reportActionsWidth();
    }
  }

  ngAfterViewInit(): void {
    this.cells.changes.pipe(startWith(null), untilDestroyed(this)).subscribe(() => {
      this.reportActionsWidth();
    });
  }

  ngOnDestroy(): void {
    this.tooltip = undefined;
    this.actionsResizeSub.unsubscribe();
  }

  getTemplate(
    columnType: ColumnType,
    templates: {
      select: TemplateRef<any>;
      actions: TemplateRef<any>;
      cell: TemplateRef<any>;
    },
  ): TemplateRef<any> {
    switch (columnType) {
      case 'select':
        return templates.select;
      case 'actions':
        return templates.actions;
      default:
        return templates.cell;
    }
  }

  copy(event: MouseEvent): void {
    const value = (event.currentTarget as HTMLElement).parentElement?.textContent?.replaceAll(
      /(^\s+|\s+$)/g,
      '',
    );

    navigator.clipboard.writeText(value ?? '').then(() => {
      this.notificationService.success(`Copied to clipboard`, true);
    });

    event.stopPropagation();
  }

  cellClick(event: MouseEvent, column: VirtualTableColumn, cell: HTMLElement): void {
    if (this.groupingLevel > -1) {
      return;
    }
    const columnLabel = column.def?.label;
    const columnName = column.def?.name ?? column.name;
    const columnId = column.def?.id;

    const columnKey = columnId ?? columnName;

    const supportedCellEditMode: CellEditMode[] = ['inline-editor', 'switch'];
    const cellsEditConfig = columnKey ? this.cellsEditConfig()?.[columnKey] : undefined;
    if (cellsEditConfig && supportedCellEditMode.includes(cellsEditConfig)) {
      this.cellEdit.emit({
        event,
        columnLabel,
        columnName,
        columnId,
        rowIndex: this.rowIndex(),
        cell,
      });
    }
  }

  toggleCellMouseDown(state: boolean): void {
    this.cellMouseDownToggled.emit(state);
  }

  checkViewportSize(): void {
    this.virtualScrollViewport?.checkViewportSize();
    this.virtualScrollStrategy?.resetViewportSize();
  }

  showTooltipWithDelay(tooltip: MatTooltip): void {
    if (this.tooltip && this.tooltip !== tooltip) {
      this.tooltip.disabled = true;
      this.tooltip.hide();
    }
    this.tooltip = tooltip;
    if (!tooltip.message) {
      return;
    }
    setTimeout(() => {
      if (this.tooltip === tooltip) {
        tooltip.disabled = false;
        tooltip.show();
      }
    }, 1500);
  }

  hideTooltip(tooltip: MatTooltip): void {
    if (this.tooltip === tooltip) {
      this.tooltip = undefined;
    }
    tooltip.disabled = true;
    tooltip?.hide();
  }

  hoverOverRowAction(rowIndex?: number | string | undefined): void {
    this.hoverCellRow.emit(rowIndex);
  }

  checkAndHoverOverRowAction(
    rowIndex: number | string | undefined,
    column: VirtualTableColumn,
  ): void {
    const index = column.def?.id ?? column.name;
    const config = this.cellsEditConfig();

    if (config?.[index]) {
      this.hoverOverRowAction(rowIndex);
    }
  }

  private reportActionsWidth(): void {
    this.ngZone.runOutsideAngular(() => {
      runAfterFramePaint(() => {
        const actionsCell = this.actionsCell?.nativeElement;

        if (!actionsCell) {
          return;
        }

        this.tableVirtualScrollFacade.updateRowActionCells(this.rowIndex.toString(), actionsCell);
      });
    });
  }

  private updateScrollPosition(): void {
    this.scrollable ??= this.registry.getInstance()?.getElementRef()?.nativeElement;

    setTimeout(() => {
      if (!this.scrollable) {
        return;
      }
      this.scrollable.dispatchEvent(new Event('scroll'));
    });
  }
}
